import { ContentPage, contentMeta, type ContentPageData } from '@/site/content'

const page: ContentPageData = {
  path: '/funktsii/zapys-i-transkryptsiya-dzvinkiv',
  parent: { name: 'Можливості', path: '/funktsii' },
  crumb: 'Запис і транскрипція',
  title: 'Запис і транскрипція дзвінків у кабінеті | Anytel',
  description:
    'Кожна розмова записана й перетворена на текст: слухайте або читайте дзвінки в кабінеті, знаходьте деталі, перевіряйте роботу агента й команди.',
  eyebrow: 'Можливості',
  h1: 'Запис і транскрипція дзвінків',
  lead:
    'Кожен дзвінок записаний і розшифрований. Замість «а що він тоді сказав?» ви відкриваєте розмову в кабінеті й за секунди знаходите потрібне: прослуховуєте або читаєте текстом.',
  sections: [
    {
      h2: 'Що записується',
      blocks: [
        'Розмови з AI-агентом і з вашими операторами, вхідні й вихідні дзвінки. Запис вмикається в сценарії, тож ви самі вирішуєте, які дзвінки записувати. На початку дзвінка звучить попередження про запис, щоб усе було чесно щодо клієнта.',
      ],
    },
    {
      h2: 'Транскрипція',
      blocks: [
        'Після дзвінка запис автоматично перетворюється на текст. Розмову з AI-агентом видно репліками: що сказав клієнт, що відповів агент, які дії він виконав. Автоматичну транскрипцію можна вимкнути в налаштуваннях і розшифровувати лише потрібні дзвінки однією кнопкою.',
      ],
    },
    {
      h2: 'Як виглядає дзвінок у кабінеті',
      blocks: [
        'Рядок у журналі дзвінків розгортається в картку з усім, що про нього відомо:',
        {
          list: [
            'хто дзвонив, на який номер, коли і скільки тривала розмова;',
            'чим закінчився дзвінок: відповів агент, оператор, клієнт поклав трубку, дзвінок пішов у чергу передзвонів;',
            'аудіоплеєр із записом і текст розмови поруч;',
            'дані, які зібрав сценарій: ім’я, послуга, бажаний час, номер замовлення;',
            'хід сценарію: якими кроками пройшов дзвінок і що натиснув клієнт.',
          ],
        },
      ],
    },
    {
      h2: 'Навіщо це бізнесу',
      blocks: [
        {
          list: [
            'Розібрати спірну ситуацію: клієнт каже, що домовлялись інакше, а ви відкрили запис і перевірили.',
            'Побачити, про що питають найчастіше, і доповнити базу знань агента.',
            'Перевірити, як агент працює на реальних дзвінках, особливо в перші дні.',
            'Навчати нових співробітників на справжніх розмовах.',
            'Не переписувати деталі вручну: адресу, номер замовлення, побажання видно в тексті.',
          ],
        },
      ],
    },
    {
      h2: 'Де зберігаються записи',
      blocks: [
        'Записи лежать у захищеному сховищі. Дані кожної компанії ізольовані, а записи бачать лише співробітники, яким ви відкрили розділ дзвінків. Слухати запис можна тільки через кабінет: посилання на файл тимчасове.',
        'Скільки зберігаються записи, залежить від тарифу. Деталі на сторінці [цін](/tsiny).',
      ],
    },
  ],
  after: [
    {
      h2: 'Перший тиждень з агентом: на що дивитися в записах',
      blocks: [
        'Найкорисніше, що можна зробити на старті, це щодня слухати чи читати кілька розмов агента. Шукайте три речі:',
        {
          list: [
            'Питання, на які агент відповів «уточню»: це прогалини в базі знань, їх варто доповнити.',
            'Моменти, де клієнт перепитував чи плутався: можливо, агент говорить задовгими фразами або не тією манерою.',
            'Переведення на людину: чи всі вони були потрібні, чи частину агент міг вирішити сам.',
          ],
        },
        'Зазвичай за кілька днів таких правок агент починає відповідати рівно так, як ви хотіли, а записи переглядаєте вже вибірково.',
      ],
    },
    {
      h2: 'Як увімкнути',
      blocks: [
        {
          steps: [
            { title: 'Запис у сценарії', text: 'У шаблонах запис уже ввімкнено разом з попередженням для абонента.' },
            { title: 'Транскрипція', text: 'Автоматична за замовчуванням, вимикається в налаштуваннях компанії.' },
            { title: 'Доступ', text: 'Видайте співробітникам доступ до розділу дзвінків: лише перегляд або з правом змін.' },
          ],
        },
        'Підсумки дзвінків можна передавати й у вашу [CRM](/funktsii/integratsiya-z-crm).',
      ],
    },
  ],
  faq: [
    {
      q: 'Чи законно записувати дзвінки?',
      a: 'Так, якщо абонента попереджено. Тому на початку дзвінка звучить коротке повідомлення про запис і віртуального асистента. Ми обробляємо записи як розпорядник ваших даних і не використовуємо їх для інших цілей.',
    },
    {
      q: 'Якими мовами працює транскрипція?',
      a: 'Українською та іншими поширеними мовами. Якість, як і в будь-якого розпізнавання мовлення, залежить від якості зв’язку.',
    },
    {
      q: 'Чи можна зберегти запис собі?',
      a: 'Так, запис відкривається в кабінеті у звичайному аудіоплеєрі, з якого його можна зберегти.',
    },
    {
      q: 'Чи можна не записувати окремі дзвінки?',
      a: 'Так. Запис вмикається кроком у сценарії, тож для окремого номера чи гілки сценарію його можна не вмикати.',
    },
    {
      q: 'Хто з команди бачить записи?',
      a: 'Лише ті, кому ви дали доступ до розділу дзвінків. Права налаштовуються для кожного співробітника.',
    },
  ],
  related: ['/funktsii/ai-agent-dlya-dzvinkiv', '/funktsii/integratsiya-z-crm', '/dlya/nerukhomosti', '/blog/ai-agent-chy-administrator'],
}

export const meta = contentMeta(page)

export default function Page() {
  return <ContentPage page={page} />
}
